// Calendario corporativo. Centro Medico → Calendarios → Calendario corporativo.
// Eventos institucionales/por área + reservas de recursos; ocupación en tiempo
// real; exportación iCal. Vistas mes/semana.
import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { CalendarRange, ChevronLeft, ChevronRight, Download, Loader2, Plus } from 'lucide-react';
import { toast } from 'sonner';
import { apiClient, getErrorMessage } from '@/lib/api/client';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import {
  AutoTextarea,
  CalItem, COLOR_EVENTO, Campo, Modal, Ocupacion, Recurso, TIPO_EVENTO_CORP, fieldCls,
  DIAS_CORTOS, addDays, addMonths, hhmm, isoDate, medRpc, med, parseISO, startOfWeek,
} from './shared';
import { LOCALE } from '@/lib/utils/regional';

type Vista = 'semana' | 'mes';
const hoyISO = () => isoDate(new Date());
const MED = { headers: { 'Content-Profile': 'medical', 'Accept-Profile': 'medical' } };

export function CalendarioCorp() {
  const [vista, setVista] = useState<Vista>('mes');
  const [fecha, setFecha] = useState(hoyISO());
  const [recursoId, setRecursoId] = useState('');
  const [nuevoEvento, setNuevoEvento] = useState(false);
  const [reservar, setReservar] = useState(false);
  const qc = useQueryClient();

  const { data: recursos = [] } = useQuery<Recurso[]>({ queryKey: ['med-recursos'], queryFn: () => medRpc<Recurso[]>('med_recursos', { _tipo: null }) });

  const [desde, hasta] = useMemo<[string, string]>(() => {
    if (vista === 'semana') { const s = startOfWeek(fecha); return [s, addDays(s, 6)]; }
    const d = parseISO(fecha); const first = isoDate(new Date(d.getFullYear(), d.getMonth(), 1));
    const last = isoDate(new Date(d.getFullYear(), d.getMonth() + 1, 0));
    return [startOfWeek(first), addDays(startOfWeek(last), 6)];
  }, [vista, fecha]);

  const { data: items = [], isFetching } = useQuery<CalItem[]>({
    queryKey: ['med-calendario', desde, hasta, recursoId],
    queryFn: () => medRpc<CalItem[]>('med_calendario', { _desde: `${desde}T00:00:00`, _hasta: `${hasta}T23:59:59`, _recurso_id: recursoId || null }),
  });

  const refrescar = () => { void qc.invalidateQueries({ queryKey: ['med-calendario'] }); void qc.invalidateQueries({ queryKey: ['med-ocupacion'] }); };
  const navega = (d: number) => setFecha(vista === 'semana' ? addDays(fecha, d * 7) : addMonths(fecha, d));

  const descargarIcs = async () => {
    const ics = (await apiClient.post<string>('/rpc/med_ical', { _recurso_id: recursoId || null }, MED)).data;
    const blob = new Blob([ics], { type: 'text/calendar' });
    const url = URL.createObjectURL(blob); const a = document.createElement('a');
    a.href = url; a.download = 'calendario.ics'; a.click(); URL.revokeObjectURL(url);
  };

  const etiqueta = vista === 'semana'
    ? `${parseISO(desde).toLocaleDateString(LOCALE, { day: 'numeric', month: 'short' })} – ${parseISO(hasta).toLocaleDateString(LOCALE, { day: 'numeric', month: 'short' })}`
    : parseISO(fecha).toLocaleDateString(LOCALE, { month: 'long', year: 'numeric' });

  return (
    <div className="p-6 space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h1 className="text-xl font-semibold flex items-center gap-2"><CalendarRange className="w-5 h-5" /> Calendario corporativo</h1>
        <div className="flex flex-wrap items-center gap-2">
          <select className={`${fieldCls} w-52`} value={recursoId} onChange={(e) => setRecursoId(e.target.value)}>
            <option value="">Todos / institucional</option>
            {recursos.map((r) => <option key={r.id} value={r.id}>{r.nombre}</option>)}
          </select>
          <Button variant="outline" size="sm" onClick={descargarIcs}><Download className="w-4 h-4 mr-1" /> iCal</Button>
          <Button size="sm" variant="outline" onClick={() => setNuevoEvento(true)}><Plus className="w-4 h-4 mr-1" /> Evento</Button>
          <Button size="sm" onClick={() => setReservar(true)}><Plus className="w-4 h-4 mr-1" /> Reservar</Button>
        </div>
      </div>

      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="inline-flex rounded-md border overflow-hidden">
          {(['semana', 'mes'] as Vista[]).map((v) => (
            <button key={v} onClick={() => setVista(v)} aria-pressed={vista === v} className={`px-3 py-1.5 text-sm capitalize ${vista === v ? 'bg-primary text-primary-foreground' : 'hover:bg-muted'}`}>{v}</button>
          ))}
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={() => navega(-1)}><ChevronLeft className="w-4 h-4" /></Button>
          <Button variant="outline" size="sm" onClick={() => setFecha(hoyISO())}>Hoy</Button>
          <Button variant="outline" size="sm" onClick={() => navega(1)}><ChevronRight className="w-4 h-4" /></Button>
          <span className="text-sm font-medium capitalize min-w-[10rem] text-center">{etiqueta}</span>
        </div>
      </div>

      <Card><CardContent className="pt-4">
        {isFetching && <div className="py-2 text-center"><Loader2 className="w-5 h-5 animate-spin mx-auto text-primary" /></div>}
        {vista === 'semana'
          ? <Semana desde={desde} items={items} />
          : <Mes fecha={fecha} desde={desde} hasta={hasta} items={items} onDia={(d) => { setFecha(d); setVista('semana'); }} />}
      </CardContent></Card>

      <OcupacionPanel />

      {nuevoEvento && <NuevoEvento recursos={recursos} onClose={() => setNuevoEvento(false)} onSaved={() => { setNuevoEvento(false); refrescar(); }} />}
      {reservar && <Reservar recursos={recursos} onClose={() => setReservar(false)} onSaved={() => { setReservar(false); refrescar(); }} />}
    </div>
  );
}

function Pill({ it }: { it: CalItem }) {
  const cls = COLOR_EVENTO[it.clase === 'reserva' ? 'reserva' : it.tipo] ?? COLOR_EVENTO.otro;
  return (
    <div className={`rounded px-1.5 py-0.5 text-xs truncate ${cls}`} title={it.titulo}>
      {!it.todo_el_dia && <span className="font-medium">{hhmm(it.fecha_inicio.slice(11))} </span>}
      {it.titulo}{it.recurso ? ` · ${it.recurso}` : ''}
    </div>
  );
}

function Semana({ desde, items }: { desde: string; items: CalItem[] }) {
  const dias = Array.from({ length: 7 }, (_, i) => addDays(desde, i));
  return (
    <div className="grid grid-cols-1 sm:grid-cols-7 gap-2">
      {dias.map((d, i) => (
        <div key={d} className="rounded-md border min-h-[8rem] flex flex-col">
          <div className={`px-2 py-1 text-xs border-b ${d === isoDate(new Date()) ? 'bg-primary/10 font-semibold' : ''}`}>{DIAS_CORTOS[i]} {parseISO(d).getDate()}</div>
          <div className="flex-1 space-y-1 p-1 overflow-y-auto">
            {items.filter((it) => it.fecha_inicio.slice(0, 10) <= d && it.fecha_fin.slice(0, 10) >= d).map((it) => <Pill key={it.clase + it.id} it={it} />)}
          </div>
        </div>
      ))}
    </div>
  );
}

function Mes({ fecha, desde, hasta, items, onDia }: { fecha: string; desde: string; hasta: string; items: CalItem[]; onDia: (d: string) => void }) {
  const mes = parseISO(fecha).getMonth();
  const dias: string[] = []; for (let d = desde; d <= hasta; d = addDays(d, 1)) dias.push(d);
  return (
    <div className="grid grid-cols-7 gap-1">
      {DIAS_CORTOS.map((d) => <div key={d} className="text-center text-xs font-medium text-muted-foreground py-1">{d}</div>)}
      {dias.map((d) => {
        const delDia = items.filter((it) => it.fecha_inicio.slice(0, 10) <= d && it.fecha_fin.slice(0, 10) >= d);
        const dt = parseISO(d); const fuera = dt.getMonth() !== mes;
        return (
          <button key={d} onClick={() => onDia(d)} className={`min-h-[5.5rem] rounded-md border p-1 text-left align-top overflow-hidden hover:border-primary ${fuera ? 'opacity-40' : ''} ${d === isoDate(new Date()) ? 'border-primary' : ''}`}>
            <div className="text-xs font-medium">{dt.getDate()}</div>
            <div className="space-y-0.5 mt-0.5">{delDia.slice(0, 3).map((it) => <Pill key={it.clase + it.id} it={it} />)}{delDia.length > 3 && <div className="text-[10px] text-muted-foreground">+{delDia.length - 3} más</div>}</div>
          </button>
        );
      })}
    </div>
  );
}

function OcupacionPanel() {
  const { data: rows = [] } = useQuery<Ocupacion[]>({ queryKey: ['med-ocupacion'], queryFn: () => medRpc<Ocupacion[]>('med_ocupacion', {}), refetchInterval: 30000 });
  return (
    <Card>
      <CardHeader className="pb-2"><CardTitle className="text-base">Ocupación en tiempo real</CardTitle></CardHeader>
      <CardContent>
        {rows.length === 0 ? <p className="text-sm text-muted-foreground py-4 text-center">Sin recursos</p> : (
          <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-3">
            {rows.map((r) => (
              <div key={r.id} className={`rounded-md border px-3 py-2 text-sm ${r.ocupado ? 'border-urgente/40 bg-urgente-soft' : 'border-estable/40 bg-estable-soft'}`}>
                <div className="flex items-center justify-between">
                  <span className="font-medium">{r.nombre}</span>
                  <span className={`text-xs ${r.ocupado ? 'text-urgente' : 'text-estable'}`}>{r.ocupado ? 'Ocupado' : 'Libre'}</span>
                </div>
                <div className="text-xs text-muted-foreground capitalize">{r.tipo}{r.ubicacion ? ` · ${r.ubicacion}` : ''}</div>
                {r.ocupado && <div className="text-xs">{r.ocupado_por}{r.hasta ? ` · hasta ${new Date(r.hasta).toLocaleTimeString(LOCALE, { hour: '2-digit', minute: '2-digit' })}` : ''}</div>}
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function NuevoEvento({ recursos, onClose, onSaved }: { recursos: Recurso[]; onClose: () => void; onSaved: () => void }) {
  const [f, setF] = useState({ titulo: '', tipo: 'reunion', recurso_id: '', fecha_inicio: '', fecha_fin: '', todo_el_dia: false, descripcion: '' });
  const guardar = useMutation({
    mutationFn: () => med.create('evento_corp', { titulo: f.titulo, tipo: f.tipo, recurso_id: f.recurso_id || null, fecha_inicio: f.fecha_inicio, fecha_fin: f.fecha_fin || f.fecha_inicio, todo_el_dia: f.todo_el_dia, descripcion: f.descripcion || null }),
    onSuccess: () => { toast.success('Evento creado'); onSaved(); }, onError: (e) => toast.error(getErrorMessage(e)),
  });
  return (
    <Modal title="Nuevo evento" onClose={onClose}>
      <div className="space-y-3">
        <Campo label="Título *"><input className={fieldCls} value={f.titulo} onChange={(e) => setF({ ...f, titulo: e.target.value })} /></Campo>
        <div className="grid grid-cols-2 gap-3">
          <Campo label="Tipo"><select className={fieldCls} value={f.tipo} onChange={(e) => setF({ ...f, tipo: e.target.value })}>{TIPO_EVENTO_CORP.map((t) => <option key={t} value={t}>{t}</option>)}</select></Campo>
          <Campo label="Recurso / área"><select className={fieldCls} value={f.recurso_id} onChange={(e) => setF({ ...f, recurso_id: e.target.value })}><option value="">Institucional</option>{recursos.map((r) => <option key={r.id} value={r.id}>{r.nombre}</option>)}</select></Campo>
          <Campo label="Inicio *"><input type="datetime-local" className={fieldCls} value={f.fecha_inicio} onChange={(e) => setF({ ...f, fecha_inicio: e.target.value })} /></Campo>
          <Campo label="Fin"><input type="datetime-local" className={fieldCls} value={f.fecha_fin} onChange={(e) => setF({ ...f, fecha_fin: e.target.value })} /></Campo>
        </div>
        <Campo label="Descripción"><AutoTextarea value={f.descripcion} onChange={(e) => setF({ ...f, descripcion: e.target.value })} /></Campo>
        <div className="flex justify-end gap-2 pt-2">
          <Button variant="outline" onClick={onClose}>Cancelar</Button>
          <Button isLoading={guardar.isPending} disabled={!f.titulo.trim() || !f.fecha_inicio} onClick={() => guardar.mutate()}>Crear</Button>
        </div>
      </div>
    </Modal>
  );
}

function Reservar({ recursos, onClose, onSaved }: { recursos: Recurso[]; onClose: () => void; onSaved: () => void }) {
  const [f, setF] = useState({ recurso_id: '', titulo: '', fecha_inicio: '', fecha_fin: '', responsable: '', motivo: '' });
  const guardar = useMutation({
    mutationFn: () => medRpc('med_reservar_recurso', { _data: f }),
    onSuccess: () => { toast.success('Recurso reservado'); onSaved(); }, onError: (e) => toast.error(getErrorMessage(e)),
  });
  return (
    <Modal title="Reservar recurso" onClose={onClose}>
      <div className="space-y-3">
        <Campo label="Recurso *"><select className={fieldCls} value={f.recurso_id} onChange={(e) => setF({ ...f, recurso_id: e.target.value })}><option value="">— Selecciona —</option>{recursos.map((r) => <option key={r.id} value={r.id}>{r.nombre} ({r.tipo})</option>)}</select></Campo>
        <Campo label="Título / motivo *"><input className={fieldCls} value={f.titulo} onChange={(e) => setF({ ...f, titulo: e.target.value })} /></Campo>
        <div className="grid grid-cols-2 gap-3">
          <Campo label="Inicio *"><input type="datetime-local" className={fieldCls} value={f.fecha_inicio} onChange={(e) => setF({ ...f, fecha_inicio: e.target.value })} /></Campo>
          <Campo label="Fin *"><input type="datetime-local" className={fieldCls} value={f.fecha_fin} onChange={(e) => setF({ ...f, fecha_fin: e.target.value })} /></Campo>
        </div>
        <Campo label="Responsable"><input className={fieldCls} value={f.responsable} onChange={(e) => setF({ ...f, responsable: e.target.value })} /></Campo>
        <div className="flex justify-end gap-2 pt-2">
          <Button variant="outline" onClick={onClose}>Cancelar</Button>
          <Button isLoading={guardar.isPending} disabled={!f.recurso_id || !f.titulo.trim() || !f.fecha_inicio || !f.fecha_fin} onClick={() => guardar.mutate()}>Reservar</Button>
        </div>
      </div>
    </Modal>
  );
}
